import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '@/shared/api/firebase';
import { publicEnv } from '@/shared/utils/env';
import type {
  EntryRule,
  LanguageCode,
  CreateEntryRuleDto,
  UpdateEntryRuleDto,
} from '../types/entryRules.types';

const ENTRY_RULES_COLLECTION = publicEnv.BUN_PUBLIC_ENTRY_RULES_COLLECTION || 'entry-rules';

const rulesRef = (lang: LanguageCode) =>
  collection(db, ENTRY_RULES_COLLECTION, lang, 'rules');

const ruleRef = (lang: LanguageCode, countryCode: string) =>
  doc(db, ENTRY_RULES_COLLECTION, lang, 'rules', countryCode);

/** Thrown when a rule would land on a country that already has one. */
export class EntryRuleConflictError extends Error {
  constructor(public readonly countryCode: string) {
    super(`Entry rule for ${countryCode} already exists`);
    this.name = 'EntryRuleConflictError';
  }
}

export async function getEntryRules(lang: LanguageCode): Promise<EntryRule[]> {
  const snapshot = await getDocs(rulesRef(lang));
  return snapshot.docs.map((d) => ({ ...(d.data() as Omit<EntryRule, 'id'>), id: d.id }));
}

export async function createEntryRule(dto: CreateEntryRuleDto): Promise<EntryRule> {
  const ref = ruleRef(dto.language, dto.countryCode);
  const now = new Date().toISOString();
  console.log('[createEntryRule] payload:', { path: ref.path, dto });
  try {
    // setDoc overwrites, so without this check a second tab (or a stale card list)
    // could silently replace an existing country's rule.
    const existing = await getDoc(ref);
    if (existing.exists()) {
      throw new EntryRuleConflictError(dto.countryCode);
    }
    await setDoc(ref, {
      countryCode: dto.countryCode,
      ruleText:    dto.ruleText,
      language:    dto.language,
      createdAt:   serverTimestamp(),
      updatedAt:   serverTimestamp(),
    });
    const result = { id: dto.countryCode, ...dto, createdAt: now, updatedAt: now };
    console.log('[createEntryRule] success:', result);
    return result;
  } catch (err) {
    console.error('[createEntryRule] error:', err);
    throw err;
  }
}

export async function updateEntryRule(
  id: string,
  lang: LanguageCode,
  dto: UpdateEntryRuleDto
): Promise<EntryRule> {
  // The country code is the document id, so changing the country means moving the
  // document, not just rewriting its fields. Rewriting in place is what left the
  // Turkey rule stored under the AU key in production.
  if (dto.countryCode !== id) {
    return moveEntryRule(id, lang, dto);
  }

  const ref = ruleRef(lang, id);
  console.log('[updateEntryRule] payload:', { path: ref.path, id, dto });
  try {
    await updateDoc(ref, {
      countryCode: dto.countryCode,
      ruleText:    dto.ruleText,
      updatedAt:   serverTimestamp(),
    });
    const snapshot = await getDoc(ref);
    const result = { ...(snapshot.data() as Omit<EntryRule, 'id'>), id: snapshot.id };
    console.log('[updateEntryRule] success:', result);
    return result;
  } catch (err) {
    console.error('[updateEntryRule] error:', err);
    throw err;
  }
}

/**
 * Moves a rule to a different country: writes the document under the new country
 * code and removes the old one in a single atomic batch, so a failure can never
 * leave both documents behind.
 */
async function moveEntryRule(
  id: string,
  lang: LanguageCode,
  dto: UpdateEntryRuleDto
): Promise<EntryRule> {
  const fromRef = ruleRef(lang, id);
  const toRef = ruleRef(lang, dto.countryCode);
  console.log('[moveEntryRule] payload:', { from: fromRef.path, to: toRef.path, dto });

  try {
    const [fromSnapshot, toSnapshot] = await Promise.all([getDoc(fromRef), getDoc(toRef)]);
    if (toSnapshot.exists()) {
      throw new EntryRuleConflictError(dto.countryCode);
    }

    const batch = writeBatch(db);
    batch.set(toRef, {
      countryCode: dto.countryCode,
      ruleText:    dto.ruleText,
      language:    lang,
      createdAt:   fromSnapshot.data()?.createdAt ?? serverTimestamp(),
      updatedAt:   serverTimestamp(),
    });
    batch.delete(fromRef);
    await batch.commit();

    const snapshot = await getDoc(toRef);
    const result = { ...(snapshot.data() as Omit<EntryRule, 'id'>), id: snapshot.id };
    console.log('[moveEntryRule] success:', result);
    return result;
  } catch (err) {
    console.error('[moveEntryRule] error:', err);
    throw err;
  }
}

export async function deleteEntryRule(id: string, lang: LanguageCode): Promise<void> {
  await deleteDoc(ruleRef(lang, id));
}
